import { useAuthProviders } from '../../api/queryHooks/user';
import { usePasskeyLogin } from '../../api/queryHooks/mfa';
import { useAccountInfo } from '../../api/queryHooks/account';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Loader, PasskeyLoginButton } from '@f2m3/shared-frontend-ui';
import PasswordLoginFlow from './PasswordLoginFlow';
import FacebookButton from './FacebookButton';
import GoogleButton from './GoogleButton';
import Register from './Register';
import { isApiError } from '../../api/fetch/apiClient';
import './socialLoginButtons.scss';

interface AvailableProvidersProps {
  email: string;
  /** Back to the email step (offered when the address can't be signed up). */
  onChangeEmail?: () => void;
}

const AvailableProviders = ({ email, onChangeEmail }: AvailableProvidersProps) => {
  const { t } = useTranslation();
  const { data: providerData, isPending: isProvidersPending, error } = useAuthProviders(email);
  const { data: accountInfo } = useAccountInfo();
  // Passkey-only accounts have no password step to fall into, so the actionable
  // passkey button lives right here (not just the top-of-page PasskeyLoginEntry,
  // which is only rendered on the earlier "email" step). Reuses the same
  // usePasskeyLogin ceremony as PasskeyLoginEntry — see that component for why
  // WebAuthn feature-detection happens here rather than in the shared button.
  const passkeyLogin = usePasskeyLogin();
  const passkeySupported = typeof window !== 'undefined' && 'PublicKeyCredential' in window;

  const providers = providerData?.providers ?? [];
  const hasPassword = providers.includes('password');
  // Mirrors the enabled-check each social button applies internally (FacebookButton /
  // GoogleButton independently return null unless the shop configured credentials).
  // AvailableProviders can't see inside those buttons, so it needs its own read of the
  // same useAccountInfo settings to know whether anything will actually render below —
  // computed once here rather than re-inlined at each call site.
  const facebookUsable =
    providers.includes('facebook') &&
    !!accountInfo?.setting?.facebook_login?.enabled &&
    !!accountInfo?.setting?.facebook_login?.app_id;
  const googleUsable =
    providers.includes('google') &&
    !!accountInfo?.setting?.google_login?.enabled &&
    !!accountInfo?.setting?.google_login?.client_id;
  // Matches the passkey block's own render condition below exactly (same expression,
  // reused rather than re-written) — that block renders a prompt + recovery link
  // whenever there's a passkey provider and no password fallback, regardless of
  // WebAuthn support, so it's never blank in that case either.
  const passkeyBlockRenders = providers.includes('passkey') && !hasPassword;
  // True only when the user genuinely has nothing actionable to click: they exist and
  // have providers (so this isn't the unknown-email/Register case), but every provider
  // they have is either password-less or has since been disabled by the shop.
  const hasNothingActionable =
    providers.length > 0 && !hasPassword && !facebookUsable && !googleUsable && !passkeyBlockRenders;

  if (isProvidersPending) {
    return <Loader />;
  }

  if (error) {
    const errorMessage =
      (isApiError(error) && typeof error.body?.error === 'string'
        ? error.body.error
        : null) || 'notify.users.account_not_found';
    return (
      <div className="notification is-warning">
        <p>{t(errorMessage)}</p>
      </div>
    );
  }

  return (
    <>
      { !providerData && <Loader /> }
      { hasPassword && <PasswordLoginFlow email={email} /> }
      <div className="social-login-buttons">
        { providerData?.providers.includes("facebook") && <FacebookButton /> }
        { providerData?.providers.includes("google") && <GoogleButton /> }
      </div>
      { passkeyBlockRenders && (
        <>
          <div className="notification is-info is-light">
            {t('login.passkey_only_prompt')}
          </div>
          { passkeySupported && (
            <PasskeyLoginButton
              label={t('login.passkey_button')}
              className="is-primary"
              isPending={passkeyLogin.isPending}
              onClick={() => passkeyLogin.mutate()}
            />
          ) }
          <p className="has-text-centered mt-3 is-size-7">
            <Link to="/forgot-password" state={{ email }}>{t('login.lost_passkey')}</Link>
          </p>
        </>
      ) }
      { hasNothingActionable && (
        <div className="notification is-warning">
          <p>{t('login.no_provider_available')}</p>
        </div>
      ) }
      { providerData?.providers.length === 0 && <Register email={email} onChangeEmail={onChangeEmail} /> }
    </>
  );
}

export default AvailableProviders;
